import type { PdfByteSource } from './pdfium-document';

/** Anything that hands out byte ranges of one file, e.g. a blob range reader. */
export interface PdfRangeReader {
  readonly size: number;
  read(start: number, endExclusive: number): Promise<Blob>;
}

/** What the renderer worker receives to open one PDF. */
export interface PdfSourceHandle {
  size: number;
  port: MessagePort;
}

type ByteRequest = { id: number; start: number; end: number };
type ByteResponse =
  | { id: number; bytes: ArrayBuffer }
  | { id: number; error: string };

/**
 * Main-thread side: answers the worker's byte requests from `reader`. The
 * returned port goes to the worker (transfer it); `close` ends serving.
 */
export function servePdfSource(reader: PdfRangeReader): {
  handle: PdfSourceHandle;
  close: () => void;
} {
  const channel = new MessageChannel();
  const port = channel.port1;
  port.onmessage = (event: MessageEvent<ByteRequest>) => {
    const { id, start, end } = event.data;
    reader
      .read(start, end)
      .then(blob => blob.arrayBuffer())
      .then(bytes => {
        port.postMessage({ id, bytes } satisfies ByteResponse, [bytes]);
      })
      .catch((error: unknown) => {
        port.postMessage({
          id,
          error: error instanceof Error ? error.message : String(error),
        } satisfies ByteResponse);
      });
  };
  return {
    handle: { size: reader.size, port: channel.port2 },
    close: () => port.close(),
  };
}

/** Worker side: byte source that asks the main thread over `handle.port`. */
export function pdfByteSourceFromHandle(
  handle: PdfSourceHandle
): PdfByteSource & { close: () => void } {
  const pending = new Map<
    number,
    { resolve: (bytes: ArrayBuffer) => void; reject: (error: Error) => void }
  >();
  let nextId = 0;
  handle.port.onmessage = (event: MessageEvent<ByteResponse>) => {
    const response = event.data;
    const request = pending.get(response.id);
    if (!request) return;
    pending.delete(response.id);
    if ('error' in response) request.reject(new Error(response.error));
    else request.resolve(response.bytes);
  };
  return {
    size: handle.size,
    read: (start, end) =>
      new Promise<ArrayBuffer>((resolve, reject) => {
        const id = nextId++;
        pending.set(id, { resolve, reject });
        handle.port.postMessage({ id, start, end } satisfies ByteRequest);
      }),
    close: () => {
      handle.port.close();
      for (const request of pending.values()) {
        request.reject(new Error('PDF source closed'));
      }
      pending.clear();
    },
  };
}
